import { useRoleCoverageData } from './useRoleCoverageData';
import { useCoverageBoard } from './useCoverageBoard';
import { useCoverageMatrix } from './useCoverageMatrix';
import { useRoleEditor } from './useRoleEditor';
import { useRoleKeyCreateForm } from './useRoleKeyCreateForm';

/**
 * EAA-1817: `/console/role-coverage` — "Liên kết vai trò", the role x
 * application coverage board (pairs with portal-api's EAA-1840).
 *
 * Composes four narrower hooks rather than one large one, per root CLAUDE.md's
 * split guidance:
 *   - `useRoleCoverageData`  — the four queries + capability flags every
 *     other piece reads from.
 *   - `useCoverageBoard`     — rail filtering/tabs + the "which app/role is
 *     selected" state, shared by the per-app and per-role coverage views.
 *   - `useCoverageMatrix`    — EAA-1817: the **Ma trận** view's own grid
 *     (standard roles × applications, frame 55a) and its filter state. It
 *     reads the SAME queries as the board — one `GET /role-map/coverage`
 *     resolving from one cache entry — and keeps its own filters, because
 *     "which slice of the grid is on screen" and "which app is open in the
 *     editor" are unrelated pieces of state.
 *   - `useRoleEditor`        — the selected app's editor card: rows, chip
 *     add/remove/restore, catalog sync, the undo window.
 *   - `useRoleKeyCreateForm` — the "Tạo vai trò chuẩn" empty-state CTA.
 *
 * EAA-1841: this is no longer its own route. `/console/role-coverage` and
 * `/console/role-map` were two renderings of ONE screen — design frames 55 and
 * 56–59 sit under the same sidebar entry ("Liên kết vai trò") and draw the same
 * rows and the same "Phạm vi" concept, and UX-SPEC §"/console/role-map — the only view" says so
 * outright. They are now the two view modes of `RoleMapPage`, and this hook
 * backs the **board** one. `active` is false while the list view is on screen.
 */
export function useRoleCoverage(active = true) {
    const data = useRoleCoverageData(active);
    const board = useCoverageBoard(data);
    const matrix = useCoverageMatrix(data);
    const editor = useRoleEditor(data, board.selectedAppRow);
    const roleKeyCreate = useRoleKeyCreateForm(data.apiBase, data.token);

    const isLoading =
        data.coverageQuery.isLoading ||
        data.roleKeysQuery.isLoading ||
        data.adminAppsQuery.isLoading;
    const isError =
        data.coverageQuery.isError || data.roleKeysQuery.isError || data.adminAppsQuery.isError;

    function retry() {
        void data.coverageQuery.refetch();
        void data.roleKeysQuery.refetch();
        void data.adminAppsQuery.refetch();
        void data.roleMapsQuery.refetch();
    }

    return {
        canView: data.canView,
        canManage: data.canManage,
        canSync: data.canSync,
        canManageRoleKeys: data.canManageRoleKeys,
        isLoading,
        isError,
        retry,
        appCount: board.appRows.length,
        roleKeyCount: board.roleKeys.length,
        board,
        matrix,
        editor,
        roleKeyCreate,
    };
}

export type RoleCoverageVM = ReturnType<typeof useRoleCoverage>;
